import { AmenityTypeImageCell } from '@/components/amenity-types';
import type { CrudConfig } from '@startupquickstart/frontend';

// Define type based on our Prisma schema
export interface AmenityType {
  id: string;
  name: string;
  description?: string;
  featuredImageId?: string;
  createdAt: string;
  updatedAt: string;
  featuredImage?: {
    id: string;
    fileName: string;
    name: string;
    fileType: string;
    url?: string;
  };
  // Virtual field for large image display on detail pages
  featuredImageLarge?: {
    id: string;
    fileName: string;
    name: string;
    fileType: string;
    url?: string;
  };
}

/**
 * Amenity Type CRUD configuration
 */
export const amenityTypeCrudConfig: CrudConfig<AmenityType> = {
  entityName: 'Amenity Type',
  entityNamePlural: 'Amenity Types',

  endpoints: {
    list: '/amenity-types',
    single: (id: string) => `/amenity-types/${id}`,
    create: '/amenity-types',
    update: (id: string) => `/amenity-types/${id}`,
    delete: (id: string) => `/amenity-types/${id}`,
  },

  fields: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter amenity type name',
      validation: {
        min: 2,
        max: 200,
      },
    },
    {
      key: 'description',
      label: 'Description',
      type: 'richtext',
      required: false,
      placeholder: 'Enter description',
    },
    {
      key: 'featuredImageId',
      label: 'Featured Image',
      type: 'image',
      required: false,
      placeholder: 'Featured Image ID',
    },
    {
      key: 'featuredImage',
      label: 'Image',
      type: 'custom',
      required: false,
      format: (_value: AmenityType['featuredImage'], fullRecord?: AmenityType) => {
        return <AmenityTypeImageCell amenityType={fullRecord} />;
      },
    },
    {
      key: 'featuredImageLarge',
      label: 'Image',
      type: 'custom',
      required: false,
      format: (_value: AmenityType['featuredImage'], fullRecord?: AmenityType) => {
        return <AmenityTypeImageCell amenityType={fullRecord} size="lg" />;
      },
    },
    {
      key: 'createdAt',
      label: 'Created At',
      type: 'datetime',
      readonly: true,
    },
    {
      key: 'updatedAt',
      label: 'Updated At',
      type: 'datetime',
      readonly: true,
    },
  ],

  listView: {
    fields: ['featuredImage', 'name', 'createdAt', 'updatedAt'],
    searchableFields: ['name'],
    sortableFields: ['name', 'createdAt', 'updatedAt'],
    defaultSort: {
      field: 'name',
      direction: 'asc',
    },
    itemsPerPage: 10,
    actions: {
      create: {
        enabled: true,
      },
      view: {
        enabled: true,
      },
      edit: {
        enabled: true,
      },
      delete: {
        enabled: true,
      },
    },
  },

  detailView: {
    sections: [
      {
        title: 'Basic Information',
        description: 'Core amenity type details',
        fields: ['featuredImageLarge', 'name', 'description'],
        defaultExpanded: true,
      },
      {
        title: 'System Information',
        description: 'Timestamps and system data',
        fields: ['createdAt', 'updatedAt'],
        defaultExpanded: true,
      },
    ],
  },

  form: {
    sections: [
      {
        title: 'General',
        description: 'General Information about the Amenity Type',
        fields: ['featuredImageId', 'name', 'description'],
        columns: 1,
      },
    ],
    validation: {
      name: {
        required: true,
        min: 2,
        max: 200,
      },
    },
  },
};
